import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'

import type { InfiniteData, UseInfiniteQueryResult, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { ICursorPaginatedResponse } from '~/types/catalog'
import type { IUaCampaign } from '~/types/ua-campaign'
import type { IFilters } from '~/views/UAManagement'

import {
  listCampaignRecommendations,
  listAdSetNames,
  listCampaignNames,
  setChangesStatus,
  setComment,
  setLaterStatus,
  setTreatmentStatus,
} from '~/api/ua-campaign'

////////// KEYS //////////

export const keys = {
  all: ['ua-campaign'] as const,
  list: () => [...keys.all, 'list'] as const,
  listAdSetNamesWithFilters: (filters: IFilters) => [...keys.all, 'listAdSetNames', JSON.stringify(filters)] as const,
  listCampaignNamesWithFilters: (filters: IFilters) =>
    [...keys.all, 'listCampaignNames', JSON.stringify(filters)] as const,
  listWithFilters: (filters: IFilters) => [...keys.all, 'list', JSON.stringify(filters)] as const,
}

////////// QUERIES //////////

export const useListCampaignRecommendationsQuery = (
  filters: IFilters
): UseInfiniteQueryResult<InfiniteData<ICursorPaginatedResponse<IUaCampaign>>> => {
  return useInfiniteQuery({
    getNextPageParam: (lastPage: ICursorPaginatedResponse<IUaCampaign>) =>
      lastPage.next_page_cursor !== '' ? lastPage.next_page_cursor : undefined,
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => listCampaignRecommendations(filters, pageParam),
    queryKey: keys.listWithFilters(filters),
  })
}

export const useListAdSetNamesQuery = (filters: IFilters): UseQueryResult<string[]> => {
  return useQuery({
    queryFn: () => listAdSetNames(filters),
    queryKey: keys.listAdSetNamesWithFilters(filters),
  })
}

export const useListCampaignNamesQuery = (filters: IFilters): UseQueryResult<string[]> => {
  return useQuery({
    queryFn: () => listCampaignNames(filters),
    queryKey: keys.listCampaignNamesWithFilters(filters),
  })
}

////////// MUTATIONS//////////

export const useSetTreatmentStatusMutation = (): UseMutationResult<
  void,
  unknown,
  { id: string; treated: boolean },
  unknown
> => useMutation({ mutationFn: setTreatmentStatus })

export const useSetChangesStatusMutation = (): UseMutationResult<
  void,
  unknown,
  { changesMade: boolean; id: string },
  unknown
> => useMutation({ mutationFn: setChangesStatus })

export const useSetLaterStatusMutation = (): UseMutationResult<
  void,
  unknown,
  { doLater: boolean; id: string },
  unknown
> => useMutation({ mutationFn: setLaterStatus })

export const useSetCommentsMutation = (): UseMutationResult<void, unknown, { comment: string; id: string }, unknown> =>
  useMutation({ mutationFn: setComment })
